/**
 * BuildingSystem - Manages structure placement
 */

import { Vector3 } from '../math/Vector3';
import { Structure, Blueprint, BLUEPRINTS } from './Structure';
import { InventoryComponent } from '../items/InventoryComponent';
import { World } from '../world/World';
import { Renderer } from '../core/Renderer';
import { TileType, StructureType } from '../types/enums';

export type BuildMode = 'none' | 'placing' | 'removing';

export interface PlaceResult {
  success: boolean;
  message: string;
  structure?: Structure;
}

export class BuildingSystem {
  private blueprints: Map<string, Blueprint> = new Map();
  private structures: Map<string, Structure> = new Map();
  private mode: BuildMode = 'none';
  private currentBlueprint: Blueprint | null = null;
  private previewPosition: Vector3 | null = null;
  private canPlaceAtPreview: boolean = false;

  constructor() {
    this.registerDefaultBlueprints();
  }

  private registerDefaultBlueprints(): void {
    for (const blueprint of BLUEPRINTS) {
      this.blueprints.set(blueprint.id, blueprint);
    }
  }

  /** Register a blueprint */
  registerBlueprint(blueprint: Blueprint): void {
    this.blueprints.set(blueprint.id, blueprint);
  }

  /** Get blueprint by ID */
  getBlueprint(id: string): Blueprint | undefined {
    return this.blueprints.get(id);
  }

  /** Get all blueprints */
  getAllBlueprints(): Blueprint[] {
    return Array.from(this.blueprints.values());
  }

  /** Enter build mode with a blueprint */
  enterBuildMode(blueprintId: string): boolean {
    const blueprint = this.blueprints.get(blueprintId);
    if (!blueprint) return false;

    this.mode = 'placing';
    this.currentBlueprint = blueprint;
    return true;
  }

  /** Enter remove mode */
  enterRemoveMode(): void {
    this.mode = 'removing';
    this.currentBlueprint = null;
  }

  /** Exit build mode */
  exitBuildMode(): void {
    this.mode = 'none';
    this.currentBlueprint = null;
    this.previewPosition = null;
  }

  /** Check if in build mode */
  isInBuildMode(): boolean {
    return this.mode !== 'none';
  }

  /** Get current mode */
  getMode(): BuildMode {
    return this.mode;
  }

  /** Get current blueprint */
  getCurrentBlueprint(): Blueprint | null {
    return this.currentBlueprint;
  }

  /** Update preview position */
  updatePreview(worldPos: Vector3, world: World): void {
    // Snap to grid
    this.previewPosition = new Vector3(
      Math.floor(worldPos.x),
      Math.floor(worldPos.y),
      0
    );

    // Check if can place
    if (this.currentBlueprint) {
      this.canPlaceAtPreview = this.canPlaceAt(this.currentBlueprint, this.previewPosition, world);
    }
  }

  /** Check if can place at position */
  canPlaceAt(blueprint: Blueprint, position: Vector3, world: World): boolean {
    // Check all tiles the structure would occupy
    for (let dx = 0; dx < blueprint.size.width; dx++) {
      for (let dy = 0; dy < blueprint.size.height; dy++) {
        const tileX = Math.floor(position.x) + dx;
        const tileY = Math.floor(position.y) + dy;
        const tile = world.getTile(tileX, tileY);

        // Must be valid tile
        if (!tile) return false;

        // Must not be water
        if (tile.type === TileType.Water) return false;

        // Must be buildable (unless there's a floor here)
        const existingStructure = this.getStructureAtTile(tileX, tileY);
        const hasFloor = existingStructure?.blueprint.structureType === StructureType.Floor;

        // If blueprint requires floor, check for it
        if (blueprint.requiresFloor && !hasFloor) return false;

        if (!tile.buildable && !hasFloor) return false;

        // Must not have structure (unless it's a floor and we're not placing another floor)
        if (tile.hasStructure() && !hasFloor) return false;

        // Check for existing structures at this position
        for (const structure of this.structures.values()) {
          for (const occupied of structure.getOccupiedTiles()) {
            if (occupied.x === tileX && occupied.y === tileY) {
              // Allow building on top of floors (but not another floor on a floor)
              if (structure.blueprint.structureType === StructureType.Floor &&
                  blueprint.structureType !== StructureType.Floor) {
                continue; // Allow this
              }
              return false;
            }
          }
        }
      }
    }

    return true;
  }

  /** Get structure at specific tile coordinates */
  private getStructureAtTile(tileX: number, tileY: number): Structure | null {
    for (const structure of this.structures.values()) {
      for (const occupied of structure.getOccupiedTiles()) {
        if (occupied.x === tileX && occupied.y === tileY) {
          return structure;
        }
      }
    }
    return null;
  }

  /** Place structure */
  place(world: World, inventory: InventoryComponent): PlaceResult {
    if (this.mode !== 'placing' || !this.currentBlueprint || !this.previewPosition) {
      return { success: false, message: 'Not in build mode' };
    }

    if (!this.canPlaceAtPreview) {
      return { success: false, message: 'Cannot place here' };
    }

    // Check materials
    for (const material of this.currentBlueprint.materials) {
      if (!inventory.hasItem(material.itemId, material.quantity)) {
        return { success: false, message: 'Missing materials' };
      }
    }

    // Remove materials
    for (const material of this.currentBlueprint.materials) {
      inventory.removeItem(material.itemId, material.quantity);
    }

    // Create structure
    const structure = new Structure(this.currentBlueprint, this.previewPosition);
    this.structures.set(structure.id, structure);

    // Mark tiles as occupied
    for (const tile of structure.getOccupiedTiles()) {
      const worldTile = world.getTile(tile.x, tile.y);
      if (worldTile) {
        worldTile.setStructure(structure.id);
      }
    }

    return { success: true, message: `Built ${this.currentBlueprint.name}`, structure };
  }

  /** Remove structure at position */
  removeAt(position: Vector3, world: World): Structure | null {
    const tileX = Math.floor(position.x);
    const tileY = Math.floor(position.y);

    for (const structure of this.structures.values()) {
      for (const occupied of structure.getOccupiedTiles()) {
        if (occupied.x === tileX && occupied.y === tileY) {
          // Remove from tiles
          for (const tile of structure.getOccupiedTiles()) {
            const worldTile = world.getTile(tile.x, tile.y);
            if (worldTile) {
              worldTile.setStructure(null);
            }
          }

          // Remove structure
          this.structures.delete(structure.id);
          return structure;
        }
      }
    }

    return null;
  }

  /** Get structure at position (prioritizes non-floor structures) */
  getStructureAt(position: Vector3): Structure | null {
    const tileX = Math.floor(position.x);
    const tileY = Math.floor(position.y);

    let floorStructure: Structure | null = null;

    for (const structure of this.structures.values()) {
      for (const occupied of structure.getOccupiedTiles()) {
        if (occupied.x === tileX && occupied.y === tileY) {
          // If it's a floor, save it but keep looking for non-floor structures
          if (structure.blueprint.structureType === StructureType.Floor) {
            floorStructure = structure;
          } else {
            // Non-floor structure takes priority
            return structure;
          }
        }
      }
    }

    // Return floor if no other structure found
    return floorStructure;
  }

  /** Check if position is blocked for a given entity type */
  isBlockedAt(x: number, y: number, isPlayer: boolean): boolean {
    const tileX = Math.floor(x);
    const tileY = Math.floor(y);

    for (const structure of this.structures.values()) {
      for (const occupied of structure.getOccupiedTiles()) {
        if (occupied.x === tileX && occupied.y === tileY) {
          return structure.blocksMovement(isPlayer);
        }
      }
    }

    return false;
  }

  /** Check if position is blocked for player. Returns null if no structure at position. */
  isBlockedForPlayer(x: number, y: number): boolean | null {
    const tileX = Math.floor(x);
    const tileY = Math.floor(y);

    // Find all structures at this position
    const structuresHere: Structure[] = [];
    for (const structure of this.structures.values()) {
      for (const occupied of structure.getOccupiedTiles()) {
        if (occupied.x === tileX && occupied.y === tileY) {
          structuresHere.push(structure);
          break;
        }
      }
    }

    if (structuresHere.length === 0) {
      return null; // No structure - let caller check tile
    }

    // If there's any structure that blocks player, position is blocked
    // But floors/doors don't block players
    for (const structure of structuresHere) {
      if (structure.blocksMovement(true)) {
        return true; // Blocked
      }
    }

    return false; // Not blocked (e.g., floor or door)
  }

  /** Check if position is blocked for enemy. Returns null if no structure at position. */
  isBlockedForEnemy(x: number, y: number): boolean | null {
    const tileX = Math.floor(x);
    const tileY = Math.floor(y);

    // Find all structures at this position
    const structuresHere: Structure[] = [];
    for (const structure of this.structures.values()) {
      for (const occupied of structure.getOccupiedTiles()) {
        if (occupied.x === tileX && occupied.y === tileY) {
          structuresHere.push(structure);
          break;
        }
      }
    }

    if (structuresHere.length === 0) {
      return null; // No structure - let caller check tile
    }

    // If there's any structure that blocks enemy, position is blocked
    for (const structure of structuresHere) {
      if (structure.blocksMovement(false)) {
        return true; // Blocked
      }
    }

    return false; // Not blocked
  }

  /** Get all structures */
  getAllStructures(): Structure[] {
    return Array.from(this.structures.values());
  }

  /** Update all structures */
  update(deltaTime: number): void {
    for (const structure of this.structures.values()) {
      structure.update(deltaTime);
    }

    // Remove destroyed structures
    for (const [id, structure] of this.structures) {
      if (structure.isDestroyed()) {
        this.structures.delete(id);
      }
    }
  }

  /** Render all structures (used when not using World's integrated rendering) */
  render(renderer: Renderer): void {
    for (const structure of this.structures.values()) {
      structure.render(renderer);
    }
  }

/** Clear all structures */
  clearStructures(): void {
    this.structures.clear();
  }

  /** Restore a structure from save data */
  restoreStructure(
    blueprintId: string,
    position: Vector3,
    health: number,
    world: World,
    campfireData?: {
      fuel: number;
      isBurning: boolean;
      cookingItem: string | null;
      rawQuantity: number;
      cookingProgress: number;
      cookedQuantity: number;
    },
    storageData?: {
      slots: ({ itemId: string; quantity: number } | null)[];
    }
  ): Structure | null {
    const blueprint = this.blueprints.get(blueprintId);
    if (!blueprint) {
      console.warn(`Unknown blueprint: ${blueprintId}`);
      return null;
    }

    // Create structure
    const structure = new Structure(blueprint, position);
    structure.health = health;

    // Restore campfire data if present
    if (campfireData && structure.campfire) {
      structure.campfire.fuel = campfireData.fuel;
      structure.campfire.isBurning = campfireData.isBurning;
      structure.campfire.cookingItem = campfireData.cookingItem;
      structure.campfire.rawQuantity = campfireData.rawQuantity ?? 0;
      structure.campfire.cookingProgress = campfireData.cookingProgress;
      structure.campfire.cookedQuantity = campfireData.cookedQuantity ?? 0;
    }

    // Restore storage inventory if present
    if (storageData && structure.storage) {
      structure.storage.fromSaveData({
        slots: storageData.slots,
        selectedSlot: 0
      });
    }

    this.structures.set(structure.id, structure);

    // Mark tiles as occupied
    for (const tile of structure.getOccupiedTiles()) {
      const worldTile = world.getTile(tile.x, tile.y);
      if (worldTile) {
        worldTile.setStructure(structure.id);
      }
    }

    return structure;
  }

  /** Render build preview */
  renderPreview(renderer: Renderer): void {
    if (this.mode !== 'placing' || !this.previewPosition || !this.currentBlueprint) return;

    const color = this.canPlaceAtPreview ? 'rgba(78, 204, 163, 0.5)' : 'rgba(231, 76, 60, 0.5)';
    const height = 16;

    // Draw preview block
    renderer.drawIsometricBlock(
      this.previewPosition,
      height,
      color,
      color,
      color
    );
  }
}
